'use client';

import { useState } from 'react';
import { BlockQuote, Box, Container, Hidden, Image, Text } from '@doist/marketist';
import { useTranslation } from 'app/i18n/client';
import { ContainerTwoColumns } from 'components/container-two-column/container-two-column';
import { FeatureRadioTile } from 'components/feature-radio-tile/feature-radio-tile';
import { FeatureRadioTileGroup } from 'components/feature-radio-tile/feature-radio-tile-group';
import { IconRadioTile } from 'components/icon-radio-tile/icon-radio-tile';
import { IconRadioTileGroup } from 'components/icon-radio-tile/icon-radio-tile-group';
import { SectionHeader } from 'components/SectionHeader/SectionHeader';
import { useLocale } from 'next-intl';
import { createValueTypeGuard } from 'services/create-value-type-guard';
import css from './quotes-section.module.css';
const radioTileGroupValues = ['carlPullein', 'francescoDAlessio', 'mikeVardy'] as const;
type IconRadioTileGroupValue = (typeof radioTileGroupValues)[number];
const typeGuard = createValueTypeGuard<IconRadioTileGroupValue>(radioTileGroupValues);
type Quote = {
  id: IconRadioTileGroupValue;
  name: string;
  imageSrc: string;
  alt: string;
  quote: string;
  title: string;
};
const quotes: Quote[] = [{
  id: 'carlPullein',
  name: 'Carl Pullein',
  imageSrc: '/static/channel-partners/headshot-carl-pullein.png',
  alt: 'socialProof.CarlPullein.alt',
  quote: 'socialProof.CarlPullein.quote',
  title: 'socialProof.CarlPullein.title'
}, {
  id: 'francescoDAlessio',
  name: 'Francesco D‘Alessio',
  imageSrc: '/static/channel-partners/headshot-francesco-dallesio.png',
  alt: 'socialProof.FrancescoDAlessio.alt',
  quote: 'socialProof.FrancescoDAlessio.quote',
  title: 'socialProof.FrancescoDAlessio.title'
}, {
  id: 'mikeVardy',
  name: 'Mike Vardy',
  imageSrc: '/static/channel-partners/headshot-mike-vardy.png',
  alt: 'socialProof.MikeVardy.alt',
  quote: 'socialProof.MikeVardy.quote',
  title: 'socialProof.MikeVardy.title'
}];
function QuotesSection() {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'channelpartners');
  const [iconGroup, setIconGroup] = useState<IconRadioTileGroupValue>('carlPullein');
  const [activeQuote, setActiveQuote] = useState<Quote>(quotes[0]);
  function iconRadioGroupChangeHandler(value: string) {
    if (typeGuard(value)) {
      setIconGroup(value);
      const quote = quotes.find(q => q.id === value);
      if (quote) setActiveQuote(quote);
    }
  }
  return <Container className={css.mainContainer} data-sentry-element="Container" data-sentry-component="QuotesSection" data-sentry-source-file="quotes-section.tsx">
            <Hidden above="md" data-sentry-element="Hidden" data-sentry-source-file="quotes-section.tsx">
                <SectionHeader size="base" title={t('socialProof.title')} level="2" data-sentry-element="SectionHeader" data-sentry-source-file="quotes-section.tsx" />
                <Container width="base" padded={false} data-sentry-element="Container" data-sentry-source-file="quotes-section.tsx">
                    <FeatureRadioTileGroup onChange={iconRadioGroupChangeHandler} value={iconGroup} data-sentry-element="FeatureRadioTileGroup" data-sentry-source-file="quotes-section.tsx">
                        {quotes.map(quote => <FeatureRadioTile key={quote.id} title={quote.name} value={quote.id} scene={<StackedQuote {...activeQuote} />} />)}
                    </FeatureRadioTileGroup>
                </Container>
            </Hidden>

            <Hidden below="md" data-sentry-element="Hidden" data-sentry-source-file="quotes-section.tsx">
                <SectionHeader title={t('socialProof.title')} level="2" data-sentry-element="SectionHeader" data-sentry-source-file="quotes-section.tsx" />
                <Container width="base" className={css.quotesContainer} data-sentry-element="Container" data-sentry-source-file="quotes-section.tsx">
                    <IconRadioTileGroup value={iconGroup} onChange={iconRadioGroupChangeHandler} data-sentry-element="IconRadioTileGroup" data-sentry-source-file="quotes-section.tsx">
                        {quotes.map(({
            id,
            name
          }) => <IconRadioTile key={id} value={id}>
                                {name}
                            </IconRadioTile>)}
                    </IconRadioTileGroup>
                    <ColumnQuote {...activeQuote} data-sentry-element="ColumnQuote" data-sentry-source-file="quotes-section.tsx" />
                </Container>
            </Hidden>
        </Container>;
}
function ColumnQuote({
  quote,
  title,
  name,
  imageSrc,
  alt
}: Quote) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'channelpartners');
  return <ContainerTwoColumns className={css.columnQuote} data-sentry-element="ContainerTwoColumns" data-sentry-component="ColumnQuote" data-sentry-source-file="quotes-section.tsx">
            <ContainerTwoColumns.BodyContainer className={css.text} data-sentry-element="unknown" data-sentry-source-file="quotes-section.tsx">
                <BlockQuote size="quote-lg" data-sentry-element="BlockQuote" data-sentry-source-file="quotes-section.tsx">{t(quote)}</BlockQuote>
                <Box data-sentry-element="Box" data-sentry-source-file="quotes-section.tsx">
                    <Text size="minor-lg" className={css.name} data-sentry-element="Text" data-sentry-source-file="quotes-section.tsx">
                        {name}
                    </Text>
                    <Text size="body-xs" className={css.title} data-sentry-element="Text" data-sentry-source-file="quotes-section.tsx">
                        {t(title)}
                    </Text>
                </Box>
            </ContainerTwoColumns.BodyContainer>
            <Image src={imageSrc} width={428} height={448} alt={alt} data-sentry-element="Image" data-sentry-source-file="quotes-section.tsx" />
        </ContainerTwoColumns>;
}
function StackedQuote({
  quote,
  title,
  name,
  imageSrc,
  alt
}: Quote) {
  const locale = useLocale();
  const {
    t
  } = useTranslation(locale, 'channelpartners');
  return <Box className={css.stackedQuote} data-sentry-element="Box" data-sentry-component="StackedQuote" data-sentry-source-file="quotes-section.tsx">
            <Box className={css.text} data-sentry-element="Box" data-sentry-source-file="quotes-section.tsx">
                <BlockQuote size="quote-lg" data-sentry-element="BlockQuote" data-sentry-source-file="quotes-section.tsx">{t(quote)}</BlockQuote>
                <Box data-sentry-element="Box" data-sentry-source-file="quotes-section.tsx">
                    <Text size="minor-lg" data-sentry-element="Text" data-sentry-source-file="quotes-section.tsx">{name}</Text>
                    <Text size="body-xs" data-sentry-element="Text" data-sentry-source-file="quotes-section.tsx">{t(title)}</Text>
                </Box>
            </Box>
            <Box data-sentry-element="Box" data-sentry-source-file="quotes-section.tsx">
                <Image src={imageSrc} width={235} height={245} alt={t(alt)} data-sentry-element="Image" data-sentry-source-file="quotes-section.tsx" />
            </Box>
        </Box>;
}
export { QuotesSection };